import { memo, MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from 'antd';
import { Icon } from 'Components/UI/Icon/Icon';
import { BimTreeNode } from 'Data/interfaces/BimViewer/IBimViewerPort';
import { useNodeVisibility } from '../../NodeVisibilityContext';
import { NodeActions, NodeAction, NodeCount, NodeLabel, NodeRow } from './styles';

const VISIBILITY_ICON = {
  visible: 'visualizar',
  hidden: 'notVisualizar',
  partial: 'visualizar',
} as const;

const ICON_COLOR = {
  visible: '#6b7280',
  hidden: '#9ca3af',
  partial: '#1a73e8',
} as const;

/** Rótulo do nó espacial: o nome IFC quando existe, senão a classe. */
export const spatialLabel = (node: BimTreeNode): string => node.name || node.ifcType;

interface INodeTitleProps {
  node: BimTreeNode;
}

/**
 * Uma linha da árvore: rótulo + ações à direita (filtro = isolar, olho = ghost).
 * O olho deixa translúcido em vez de sumir, é o ghost por nó.
 *
 * Memoizado de propósito. Expandir um `type-group` recria o `treeData` inteiro,
 * e sem o `memo` isso re-renderizaria toda linha montada — Tooltip e Icon
 * inclusos — por causa de um nó que mudou. Para o memo valer, a única prop é o
 * `node`, cuja identidade só muda quando a árvore muda de verdade.
 */
const NodeTitleComponent = ({ node }: INodeTitleProps) => {
  const { t } = useTranslation('viewer');
  const { visibilityOf, toggleVisibility, isIsolated, toggleIsolation } = useNodeVisibility();

  const visibility = visibilityOf(node);
  const isolated = isIsolated(node);
  const isolationLabel = t(`bim3dTree.isolation.${isolated ? 'isolated' : 'isolate'}`);

  // O clique nas ações NÃO pode selecionar o nó — sem o stopPropagation, esconder
  // um pavimento também enquadraria a câmera nele.
  const handleVisibility = (event: MouseEvent) => {
    event.stopPropagation();
    toggleVisibility(node);
  };

  const handleIsolation = (event: MouseEvent) => {
    event.stopPropagation();
    toggleIsolation(node);
  };

  return (
    <NodeRow>
      <NodeLabel $dimmed={visibility === 'hidden'}>
        {node.kind === 'element' ? node.name : (
          <>
            {node.kind === 'type-group' ? node.ifcType : spatialLabel(node)}
            <NodeCount>{node.elementCount}</NodeCount>
          </>
        )}
      </NodeLabel>
      <NodeActions $visible={isolated || visibility !== 'visible'}>
        <Tooltip title={isolationLabel} placement="top">
          <NodeAction type="button" aria-label={isolationLabel} onClick={handleIsolation}>
            <Icon
              icon={isolated ? 'filterNot' : 'filtrar'}
              customSize={13}
              customColor={isolated ? ICON_COLOR.partial : ICON_COLOR.visible}
            />
          </NodeAction>
        </Tooltip>
        <Tooltip title={t(`bim3dTree.visibility.${visibility}`)} placement="top">
          <NodeAction
            type="button"
            aria-label={t(`bim3dTree.visibility.${visibility}`)}
            onClick={handleVisibility}
          >
            <Icon
              icon={VISIBILITY_ICON[visibility]}
              customSize={13}
              customColor={ICON_COLOR[visibility]}
            />
          </NodeAction>
        </Tooltip>
      </NodeActions>
    </NodeRow>
  );
};

export const NodeTitle = memo(NodeTitleComponent);
